import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { createProceduralModel, createSingleGlbContainer } from './sampleGenerator.js';
import { getInspectorMaterial, scanAvailableTextureModes } from './inspectorShaders.js';

/**
 * Handles GLTF / GLB loading, LOD hierarchy parsing, Bounding Box Centering,
 * and robust Material Inspection Suite (PBR, Combined Normals, Tangents, UVs, unpacked ORM channels).
 */

export class LODManager {
  constructor() {
    this.gltfLoader = new GLTFLoader();
    this.currentModelData = null;
    this.lodMap = new Map(); // level => THREE.Object3D
    this.activeLodLevel = 0;
    this.currentShadingMode = 'pbr';
    this.currentEmissiveIntensity = 1.0;
    this.baseStats = { triangles: 0, vertices: 0 };
    this.modelBoundingBox = new THREE.Box3();
    this.modelCenter = new THREE.Vector3();
    this.modelSize = new THREE.Vector3();
    this.modelRadius = 1.0;
  }

  async loadModel(modelConfig) {
    this.currentModelData = modelConfig;
    this.lodMap.clear();

    if (modelConfig.type === 'hybrid' || (modelConfig.files && Array.isArray(modelConfig.files))) {
      const files = modelConfig.files || [];
      for (let i = 0; i < files.length; i++) {
        const fileEntry = files[i];
        const filePath = typeof fileEntry === 'string' ? fileEntry : fileEntry.file;
        const defaultLevel = typeof fileEntry === 'object' && fileEntry.level !== undefined ? fileEntry.level : i;

        try {
          const gltf = await this.gltfLoader.loadAsync(filePath);
          const found = this.parseInternalLODNodes(gltf.scene);
          if (found === 0) {
            this.lodMap.set(defaultLevel, gltf.scene);
          }
        } catch (err) {
          console.warn(`Could not load GLB file ${filePath}, using procedural fallback.`, err);
        }
      }

      if (this.lodMap.size === 0) {
        this.loadProceduralFallback(modelConfig);
      }

    } else if (modelConfig.type === 'single-file') {
      let rootScene;
      if (modelConfig.file && modelConfig.file !== 'procedural') {
        try {
          const gltf = await this.gltfLoader.loadAsync(modelConfig.file);
          rootScene = gltf.scene;
        } catch (err) {
          console.warn(`Could not fetch external file ${modelConfig.file}. Falling back to Single GLB Container generator.`, err);
          rootScene = createSingleGlbContainer(6);
        }
      } else {
        rootScene = createSingleGlbContainer(6);
      }

      this.parseInternalLODNodes(rootScene);

    } else if (modelConfig.type === 'multi-file') {
      const lods = modelConfig.lods || [];
      for (let i = 0; i < lods.length; i++) {
        const item = lods[i];
        try {
          const gltf = await this.gltfLoader.loadAsync(item.file);
          const found = this.parseInternalLODNodes(gltf.scene);
          if (found === 0) {
            this.lodMap.set(item.level, gltf.scene);
          }
        } catch (err) {
          const procNode = createProceduralModel(modelConfig.id, item.level, lods.length);
          this.lodMap.set(item.level, procNode);
        }
      }

    } else {
      this.loadProceduralFallback(modelConfig);
    }

    // Preserve and normalize original PBR materials across all loaded LOD scenes
    this.cacheOriginalMaterials();

    // Centering & Bounding Box calculation across all LODs
    this.centerAndAlignAllLODs();

    // Apply active shading mode & emissive intensity across all LODs
    this.applyShadingMode(this.currentShadingMode);
    this.setEmissiveIntensity(this.currentEmissiveIntensity);

    // Calculate base statistics from LOD0
    const lod0Node = this.getLodMesh(0);
    if (lod0Node) {
      this.baseStats = this.computeMeshStats(lod0Node);
    } else {
      this.baseStats = { triangles: 1000, vertices: 500 };
    }

    return this.getAvailableLodLevels();
  }

  /**
   * Synchronizes emissive brightness independently across ALL LOD levels (LOD0..LOD N) simultaneously.
   */
  setEmissiveIntensity(intensityVal) {
    this.currentEmissiveIntensity = intensityVal;

    this.lodMap.forEach((scene) => {
      scene.traverse((node) => {
        if (node.isMesh && node.material) {
          const mats = Array.isArray(node.material) ? node.material : [node.material];
          mats.forEach((m) => {
            if (m.emissiveMap || (m.emissive && (m.emissive.r > 0 || m.emissive.g > 0 || m.emissive.b > 0))) {
              m.emissiveIntensity = intensityVal;
              m.needsUpdate = true;
            }
          });
        }
      });
    });
  }

  /**
   * Caches original PBR materials into userData and synchronizes material parameters
   * (color, roughness, metalness, emissive, aoMap, colorSpace) from LOD 0 to all lower LODs.
   */
  cacheOriginalMaterials() {
    const lod0MaterialMap = new Map();
    const lod0 = this.lodMap.get(0);

    if (lod0) {
      lod0.traverse((node) => {
        if (node.isMesh && node.material) {
          const mats = Array.isArray(node.material) ? node.material : [node.material];
          mats.forEach(m => {
            if (m.name && !lod0MaterialMap.has(m.name)) {
              lod0MaterialMap.set(m.name, m);
            }
          });
        }
      });
    }

    this.lodMap.forEach((scene) => {
      scene.traverse((node) => {
        if (node.isMesh && node.material) {
          const mats = Array.isArray(node.material) ? node.material : [node.material];

          mats.forEach((m) => {
            const refMat = (m.name && lod0MaterialMap.has(m.name)) ? lod0MaterialMap.get(m.name) : null;

            if (refMat && refMat !== m) {
              if (m.color && refMat.color) m.color.copy(refMat.color);
              if (refMat.roughness !== undefined) m.roughness = refMat.roughness;
              if (refMat.metalness !== undefined) m.metalness = refMat.metalness;

              if (m.emissive && refMat.emissive) m.emissive.copy(refMat.emissive);
              if (refMat.emissiveIntensity !== undefined) m.emissiveIntensity = refMat.emissiveIntensity;

              if (refMat.aoMapIntensity !== undefined) m.aoMapIntensity = refMat.aoMapIntensity;
              if (refMat.transparent !== undefined) m.transparent = refMat.transparent;
              if (refMat.opacity !== undefined) m.opacity = refMat.opacity;
            }

            if (m.map) {
              m.map.colorSpace = THREE.SRGBColorSpace;
              m.map.needsUpdate = true;
            }

            if (m.emissiveMap) {
              m.emissiveMap.colorSpace = THREE.SRGBColorSpace;
              m.emissiveMap.needsUpdate = true;

              if (m.emissive && (m.emissive.r === 0 && m.emissive.g === 0 && m.emissive.b === 0)) {
                if (refMat && refMat.emissive && (refMat.emissive.r > 0 || refMat.emissive.g > 0 || refMat.emissive.b > 0)) {
                  m.emissive.copy(refMat.emissive);
                } else {
                  m.emissive.setHex(0xffffff);
                }
              }

              if (!m.emissiveIntensity || m.emissiveIntensity === 0) {
                m.emissiveIntensity = (refMat && refMat.emissiveIntensity) ? refMat.emissiveIntensity : 1.0;
              }
            }
          });

          if (!node.userData.originalMaterial) {
            if (Array.isArray(node.material)) {
              node.userData.originalMaterial = node.material.map(m => m.clone());
            } else {
              node.userData.originalMaterial = node.material.clone();
            }
          }
        }
      });
    });
  }

  getDetectedTextureModes() {
    return scanAvailableTextureModes(this.lodMap);
  }

  applyShadingMode(modeKey) {
    this.currentShadingMode = modeKey;

    this.lodMap.forEach((scene) => {
      scene.traverse((node) => {
        if (node.isMesh && node.userData.originalMaterial) {
          node.material = getInspectorMaterial(node.userData.originalMaterial, modeKey);
        }
      });
    });

    // Re-apply current emissive intensity after shading mode change
    this.setEmissiveIntensity(this.currentEmissiveIntensity);
  }

  centerAndAlignAllLODs() {
    const lod0Node = this.lodMap.get(0) || Array.from(this.lodMap.values())[0];
    if (!lod0Node) return;

    lod0Node.updateMatrixWorld(true);
    this.modelBoundingBox.setFromObject(lod0Node);

    if (this.modelBoundingBox.isEmpty()) return;

    this.modelBoundingBox.getCenter(this.modelCenter);
    this.modelBoundingBox.getSize(this.modelSize);

    const maxDim = Math.max(this.modelSize.x, this.modelSize.y, this.modelSize.z);
    this.modelRadius = maxDim > 0 ? maxDim / 2 : 1.0;

    const autoCenter = this.currentModelData.autoCenter !== false;
    const centerOrigin = this.currentModelData.centerOrigin || 'bottom';

    if (!autoCenter || centerOrigin === 'none') return;

    let offsetX = -this.modelCenter.x;
    let offsetY = -this.modelBoundingBox.min.y;
    let offsetZ = -this.modelCenter.z;

    if (centerOrigin === 'center') {
      offsetY = -this.modelCenter.y;
    }

    this.lodMap.forEach((node) => {
      node.position.set(offsetX, offsetY, offsetZ);
    });
  }

  loadProceduralFallback(modelConfig) {
    const count = modelConfig.lodCount || 6;
    for (let i = 0; i < count; i++) {
      const node = createProceduralModel(modelConfig.id, i, count);
      this.lodMap.set(i, node);
    }
  }

  parseInternalLODNodes(rootScene) {
    let foundLods = 0;

    rootScene.traverse((child) => {
      if (child.name) {
        const match = child.name.match(/LOD[_\-\s]?(\d+)/i);
        if (match) {
          const level = parseInt(match[1], 10);
          this.lodMap.set(level, child);
          foundLods++;
        }
      }
    });

    return foundLods;
  }

  getAvailableLodLevels() {
    const levels = Array.from(this.lodMap.keys()).sort((a, b) => a - b);
    return levels.length > 0 ? levels : [0];
  }

  getLodMesh(level) {
    if (this.lodMap.has(level)) {
      const mesh = this.lodMap.get(level);
      mesh.visible = true;
      return mesh;
    }
    return this.lodMap.get(0) || null;
  }

  computeMeshStats(object3D) {
    let triangles = 0;
    let vertices = 0;

    if (!object3D) return { triangles, vertices };

    object3D.traverse((node) => {
      if (node.isMesh && node.geometry) {
        const geometry = node.geometry;

        if (geometry.index) {
          triangles += geometry.index.count / 3;
        } else if (geometry.attributes.position) {
          triangles += geometry.attributes.position.count / 3;
        }

        if (geometry.attributes.position) {
          vertices += geometry.attributes.position.count;
        }
      }
    });

    return {
      triangles: Math.floor(triangles),
      vertices: Math.floor(vertices)
    };
  }

  getLodTelemetry(level) {
    const meshNode = this.getLodMesh(level);
    const stats = this.computeMeshStats(meshNode);

    const baseTris = Math.max(1, this.baseStats.triangles);
    const baseVerts = Math.max(1, this.baseStats.vertices);

    const reductionPercent = level === 0 ? 0 : Math.max(0, Math.min(99.9, ((baseTris - stats.triangles) / baseTris) * 100));
    const vertReductionPercent = level === 0 ? 0 : Math.max(0, Math.min(99.9, ((baseVerts - stats.vertices) / baseVerts) * 100));

    const bytesPerVertex = 48;
    const memMb = ((stats.vertices * bytesPerVertex + stats.triangles * 3 * 4) / (1024 * 1024)).toFixed(2);

    return {
      level,
      triangles: stats.triangles,
      vertices: stats.vertices,
      baseTriangles: baseTris,
      baseVertices: baseVerts,
      reductionPercent: reductionPercent.toFixed(1),
      vertReductionPercent: vertReductionPercent.toFixed(1),
      memMb
    };
  }
}
